// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FilterMenuButton } from '@/components/filter-menu-button';
import { NumberTextField } from '@/components/number-text-field';
import { formatCurrency } from '@/utilities/number';
import { Box, Button, Popover, Slider, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

export type AmountRange = [number | null, number | null];

interface Props {
  label: string;
  // Hard floor and ceiling for the slider track. Inputs cannot drag values
  // outside this range, but the value tuple itself uses null to mean
  // "no bound" so an unset filter does not implicitly clamp to [min, max].
  min: number;
  max: number;
  step: number;
  // When true, the trigger button renders disabled (used while the listing
  // response that provides the data-driven bounds has not yet arrived, or
  // when there are no charges at all).
  isDisabled?: boolean;
  value: AmountRange;
  onChange: (value: AmountRange) => void;
}

/**
 * Popover-based amount range filter. Shows a slider plus paired min/max
 * currency inputs and exposes the active state via a chip count on the
 * trigger button. The component is purely controlled — the parent owns the
 * URL/query state and decides when to clear or apply.
 */
export function FilterAmountRangePopover(props: Props) {
  const { label, min, max, step, isDisabled, value, onChange } = props;
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  // Local draft state so the slider/inputs feel snappy and the parent only
  // sees the resolved value after debouncing (or immediately on reset/blur).
  // The draft re-syncs whenever the popover is reopened so external changes
  // (e.g. clear-filters) are reflected. A URL-persisted filter from an older
  // data snapshot can carry values outside the current data-driven bounds;
  // clampToBounds keeps the slider thumb in range in that case.
  const clampToBounds = useCallback(
    (low: number | null, high: number | null): [number, number] => {
      const rawLow = low ?? min;
      const rawHigh = high ?? max;
      const clampedLow = Math.min(Math.max(rawLow, min), max);
      const clampedHigh = Math.min(Math.max(rawHigh, min), max);
      // Preserve ordering if the URL values crossed over after clamping.
      if (clampedLow > clampedHigh) {
        return [clampedHigh, clampedHigh];
      }
      return [clampedLow, clampedHigh];
    },
    [min, max]
  );

  const [draft, setDraft] = useState<[number, number]>(() =>
    clampToBounds(value[0], value[1])
  );

  useEffect(() => {
    if (open) {
      setDraft(clampToBounds(value[0], value[1]));
    }
  }, [open, value, clampToBounds]);

  const isActive = value[0] !== null || value[1] !== null;

  // Translates a draft tuple into the parent-facing nullable shape, collapsing
  // bounds that match the slider extents back to null so the URL stays clean.
  const toExternalValue = useCallback(
    (next: [number, number]): AmountRange => {
      const [low, high] = next;
      const clearedLow = low <= min ? null : low;
      const clearedHigh = high >= max ? null : high;
      if (clearedLow === null && clearedHigh === null) {
        return [null, null];
      }
      return [clearedLow ?? min, clearedHigh ?? max];
    },
    [min, max]
  );

  const commitNow = useCallback(
    (next: [number, number]) => {
      onChange(toExternalValue(next));
    },
    [onChange, toExternalValue]
  );

  // Dragging only updates local draft state — no parent onChange, no URL
  // write, no backend query. That keeps the thumb glued to the cursor
  // even on a slow network. The parent commit happens once on release
  // via onChangeCommitted below.
  const handleSlider = (_: Event, next: number | number[]) => {
    if (!Array.isArray(next)) {
      return;
    }
    const tuple: [number, number] = [next[0], next[1]];
    setDraft(tuple);
  };

  // `onChangeCommitted` fires on mouse-up / touch-end / keyboard commit —
  // this is the single point where the slider emits a value to the parent.
  const handleSliderCommitted = (
    _: Event | React.SyntheticEvent,
    next: number | number[]
  ) => {
    if (!Array.isArray(next)) {
      return;
    }
    commitNow([next[0], next[1]]);
  };

  const handleMinInput = (value: number | null) => {
    if (value === null || Number.isNaN(value)) {
      return;
    }
    const clamped = Math.max(min, Math.min(value, draft[1]));
    const tuple: [number, number] = [clamped, draft[1]];
    setDraft(tuple);
    commitNow(tuple);
  };

  const handleMaxInput = (value: number | null) => {
    if (value === null || Number.isNaN(value)) {
      return;
    }
    const clamped = Math.min(max, Math.max(value, draft[0]));
    const tuple: [number, number] = [draft[0], clamped];
    setDraft(tuple);
    commitNow(tuple);
  };

  const handleReset = () => {
    setDraft([min, max]);
    commitNow([min, max]);
  };

  return (
    <>
      <FilterMenuButton
        ref={anchorRef}
        count={isActive ? 1 : 0}
        isDisabled={isDisabled ?? false}
        onClick={() => setOpen((previous) => !previous)}
      >
        {label}
      </FilterMenuButton>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { overflow: 'visible' } } }}
      >
        <Stack spacing={2} sx={{ p: 3, width: 400 }}>
          <Typography variant="body2" fontWeight={600}>
            Amount range
          </Typography>
          <Slider
            value={draft}
            onChange={handleSlider}
            onChangeCommitted={handleSliderCommitted}
            min={min}
            max={max}
            step={step}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => formatCurrency(v, { digits: 0 })}
            slotProps={{
              valueLabel: {
                style: { whiteSpace: 'nowrap' },
              },
            }}
          />
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <NumberTextField
                size="small"
                label="Min"
                value={draft[0]}
                onChange={(values) => handleMinInput(values.floatValue ?? null)}
                showDollarPrefix
                fullWidth
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              to
            </Typography>
            <Box sx={{ flex: 1 }}>
              <NumberTextField
                size="small"
                label="Max"
                value={draft[1]}
                onChange={(values) => handleMaxInput(values.floatValue ?? null)}
                showDollarPrefix
                fullWidth
              />
            </Box>
          </Stack>
          <Stack direction="row" justifyContent="flex-end">
            <Button size="small" onClick={handleReset} disabled={!isActive}>
              Reset
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}
